/**
 * Shared Sharetribe query safety: 429 retry with backoff/jitter, Retry-After,
 * in-flight de-dupe, and a short cache. A failed fetch is never an empty list.
 */

import { isTooManyRequestsError } from './errors';

const DEFAULT_BASE_MS = 1000;
const DEFAULT_MAX_MS = 15000;
const DEFAULT_RETRIES = 2;
const DEFAULT_CACHE_TTL_MS = 15000;

const inflight = new Map();
const cache = new Map();

let cooldownUntil = 0;
const cooldownListeners = new Set();
let retryTail = Promise.resolve();

/**
 * Milliseconds left on the shared 429 cooldown, or 0.
 *
 * @returns {number}
 */
export const getSharetribeCooldownMs = () => Math.max(0, cooldownUntil - Date.now());

/**
 * Subscribe to cooldown changes (busy-retrying UI).
 *
 * @param {Function} listener
 * @returns {Function} unsubscribe
 */
export const subscribeSharetribeCooldown = listener => {
  if (typeof listener !== 'function') {
    return () => {};
  }
  cooldownListeners.add(listener);
  return () => cooldownListeners.delete(listener);
};

const notifyCooldown = () => {
  const ms = getSharetribeCooldownMs();
  cooldownListeners.forEach(listener => {
    try {
      listener(ms);
    } catch (e) {
      // UI listeners must not break retry.
    }
  });
};

/**
 * Record a Sharetribe 429 so every caller waits on the same Retry-After window.
 *
 * @param {Object} [error]
 * @param {number} [attempt]
 * @param {Object} [options]
 * @returns {number} wait ms
 */
export const noteSharetribe429 = (error, attempt = 0, options = {}) => {
  const wait = retryAfterMs(error, attempt, options);
  const next = Date.now() + (Number.isFinite(wait) && wait > 0 ? wait : 0);
  if (next > cooldownUntil) {
    cooldownUntil = next;
    notifyCooldown();
  }
  return wait;
};

const enqueueRetry = task => {
  const run = () => Promise.resolve().then(task);
  const next = retryTail.then(run, run);
  retryTail = next.then(
    () => undefined,
    () => undefined
  );
  return next;
};

/**
 * Delay before the next 429 retry. Honors Retry-After when present.
 *
 * @param {Object} [error]
 * @param {number} [attempt]
 * @param {Object} [options]
 * @returns {number}
 */
export const retryAfterMs = (error, attempt = 0, options = {}) => {
  const baseMs = Number.isFinite(options.baseMs) ? options.baseMs : DEFAULT_BASE_MS;
  const maxMs = Number.isFinite(options.maxMs) ? options.maxMs : DEFAULT_MAX_MS;
  const header =
    error?.headers?.['retry-after'] ??
    error?.headers?.['Retry-After'] ??
    error?.retryAfter ??
    error?.data?.retryAfter;
  const fromHeader = Number(header);
  if (Number.isFinite(fromHeader) && fromHeader >= 0) {
    return fromHeader >= 50 ? fromHeader : fromHeader * 1000;
  }
  const exp = Math.min(maxMs, baseMs * 2 ** attempt);
  const jitterCap = Math.min(400, Math.floor(exp * 0.25));
  const jitter = options.jitter === 0 ? 0 : Math.floor(Math.random() * (jitterCap + 1));
  return exp + jitter;
};

/**
 * Run `work`, retrying 429s with backoff. Other errors throw immediately.
 *
 * @param {Function} work
 * @param {Object} [options]
 * @returns {Promise<*>}
 */
export const retryOn429 = async (work, options = {}) => {
  const retries = Number.isFinite(options.retries) ? options.retries : DEFAULT_RETRIES;
  const retryOptions = {
    baseMs: Number.isFinite(options.waitMs) ? options.waitMs : options.baseMs,
    maxMs: options.maxMs,
    jitter: options.jitter,
  };
  const runWork = () => Promise.resolve().then(work);
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

  // inQueue: already running as the shared retry queue's current task. Queuing
  // again from here would wait on ourselves forever (Oct 24: wedged SSR /dashboard).
  const execute = async (inQueue = false) => {
    if (options.waitMs !== 0) {
      const cool = getSharetribeCooldownMs();
      if (cool > 0) {
        await sleep(cool);
      }
    }

    try {
      return await runWork();
    } catch (error) {
      if (!isTooManyRequestsError(error) || retries <= 0) {
        throw error;
      }
      noteSharetribe429(error, 0, retryOptions);
      const retryLoop = async () => {
        let lastError = error;
        for (let attempt = 1; attempt <= retries; attempt += 1) {
          if (options.waitMs !== 0) {
            const delay =
              getSharetribeCooldownMs() || retryAfterMs(lastError, attempt - 1, retryOptions);
            await sleep(delay);
          }
          try {
            return await runWork();
          } catch (nextError) {
            lastError = nextError;
            if (!isTooManyRequestsError(nextError) || attempt === retries) {
              throw nextError;
            }
            noteSharetribe429(nextError, attempt, retryOptions);
          }
        }
        throw lastError;
      };
      return inQueue ? retryLoop() : enqueueRetry(retryLoop);
    }
  };

  // After a 429, line up new callers so they do not all fire when the window lifts.
  if (options.waitMs !== 0 && getSharetribeCooldownMs() > 0) {
    return enqueueRetry(() => execute(true));
  }
  return execute();
};

/**
 * De-dupe in-flight calls and cache successful results briefly.
 *
 * @param {string} key
 * @param {Function} work
 * @param {Object} [options]
 * @returns {Promise<*>}
 */
export const dedupeSharetribeQuery = (key, work, options = {}) => {
  const ttlMs = Number.isFinite(options.ttlMs) ? options.ttlMs : DEFAULT_CACHE_TTL_MS;
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.ts < ttlMs) {
    return Promise.resolve(hit.value);
  }
  if (inflight.has(key)) {
    return inflight.get(key);
  }
  const pending = Promise.resolve(work())
    .then(value => {
      cache.set(key, { value, ts: Date.now() });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, pending);
  return pending;
};

/**
 * Clear de-dupe/cache (tests).
 */
export const clearSharetribeQueryCache = () => {
  cache.clear();
  inflight.clear();
  cooldownUntil = 0;
  retryTail = Promise.resolve();
  notifyCooldown();
};

/**
 * Failed lookup sentinel. Callers must not treat this as [].
 *
 * @param {Object} [error]
 * @returns {{ ok: false, busy: true, data: null, error: Object|null }}
 */
export const busyQueryResult = error => ({
  ok: false,
  busy: true,
  data: null,
  error: error || null,
});

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export const isBusyQueryResult = value =>
  !!value && typeof value === 'object' && value.busy === true && value.ok === false;
